import type { FileInfo, TaskSubmissionState, TaskSummary } from '../../shared/tasks';
import { TASK_MAX_FILE_MB } from '../../shared/tasks';
import { ApiError, getToken } from './api';

/**
 * Cliente de las tareas.
 *
 * Quien entrega no necesita cuenta: al entrar, el servidor da un token de su
 * entrega que se guarda en este navegador. Con él vuelve a abrir la tarea,
 * descarga su práctica, cambia el archivo o ve la nota y el comentario.
 *
 * Una tarea solo se abre con su enlace (o el QR que lo lleva): no hay PIN.
 */

const KEY = (code: string) => `dahu_task_${code}`;

export interface SavedSubmission {
  submissionId: string;
  token: string;
}

export function savedSubmission(code: string): SavedSubmission | null {
  try {
    const raw = localStorage.getItem(KEY(code));
    return raw ? (JSON.parse(raw) as SavedSubmission) : null;
  } catch {
    return null;
  }
}

export function saveSubmission(code: string, s: SavedSubmission): void {
  try {
    localStorage.setItem(KEY(code), JSON.stringify(s));
  } catch {
    /* sin almacenamiento: la entrega se ve mientras dure la pestaña */
  }
}

export function forgetSubmission(code: string): void {
  try {
    localStorage.removeItem(KEY(code));
  } catch {
    /* nada que borrar */
  }
}

/** Enlace de una tarea: la única forma de entrar a ella. */
export function taskUrl(code: string): string {
  return `${location.origin}/tarea/${encodeURIComponent(code)}`;
}

async function call<T>(
  path: string,
  init: RequestInit & { taskToken?: string; asBlob?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof Blob) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (init.taskToken) headers.set('X-Task-Token', init.taskToken);
  const session = getToken();
  if (session) headers.set('Authorization', `Bearer ${session}`);

  const res = await fetch(`/api${path}`, { ...init, headers });
  if (res.ok && init.asBlob) return (await res.blob()) as T;
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* sin cuerpo */
  }
  if (!res.ok) {
    const message =
      (data as { error?: string } | null)?.error ??
      (res.status === 413 ? 'El archivo es demasiado grande.' : `Error ${res.status}`);
    throw new ApiError(message, res.status);
  }
  return data as T;
}

const isPdfName = (name: string) => /\.pdf$/i.test(name);

/**
 * Abre un archivo que pide cabeceras (sesión o token). El PDF se ve en una
 * pestaña nueva; el Word se descarga con su nombre. La pestaña se abre antes
 * de pedirlo: si se abre después de esperar, el navegador la bloquea.
 */
export async function openFile(name: string, load: () => Promise<Blob>): Promise<void> {
  const pdf = isPdfName(name);
  const tab = pdf ? window.open('', '_blank') : null;
  try {
    const blob = await load();
    const url = URL.createObjectURL(pdf ? new Blob([blob], { type: 'application/pdf' }) : blob);
    if (pdf && tab) tab.location.href = url;
    else {
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/** Un archivo elegido en el navegador, comprobado antes de subirlo. */
export function checkTaskFile(file: File | undefined): string | null {
  if (!file) return null;
  if (!/\.(pdf|docx?)$/i.test(file.name)) return 'Tiene que ser un PDF o un documento de Word.';
  if (file.size > TASK_MAX_FILE_MB * 1024 * 1024) return `El archivo pasa de ${TASK_MAX_FILE_MB} MB.`;
  return null;
}

/** Cuánto lleva subido, de 0 a 1. */
export type OnProgress = (fraction: number) => void;

/**
 * Sube un archivo con PUT. Va por XMLHttpRequest y no por fetch porque fetch
 * no dice cuánto lleva subido, y con un archivo grande y una conexión lenta
 * hace falta enseñarlo: si no, parece que no pasa nada.
 */
function putFile<T>(path: string, file: File, opts: { taskToken?: string; headers?: Record<string, string>; onProgress?: OnProgress } = {}): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `/api${path}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    const session = getToken();
    if (session) xhr.setRequestHeader('Authorization', `Bearer ${session}`);
    if (opts.taskToken) xhr.setRequestHeader('X-Task-Token', opts.taskToken);
    for (const [k, v] of Object.entries(opts.headers ?? {})) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: { error?: string } | null = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* sin cuerpo JSON: nginx responde en HTML */
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T);
      reject(
        new ApiError(
          data?.error ??
            (xhr.status === 413 ? `El archivo pasa de ${TASK_MAX_FILE_MB} MB.` : `Error ${xhr.status} al subir el archivo.`),
          xhr.status,
        ),
      );
    };
    xhr.onerror = () => reject(new ApiError('Se cortó la conexión mientras subía. Revisa el internet y vuelve a intentarlo.', 0));
    xhr.ontimeout = xhr.onerror;
    xhr.send(file);
  });
}

export const taskApi = {
  start: (body: { code: string; studentId?: string; firstName?: string; lastName?: string }) =>
    call<{ token: string; state: TaskSubmissionState }>('/task-access/start', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  state: (s: SavedSubmission) =>
    call<{ state: TaskSubmissionState }>(`/task-submissions/${s.submissionId}`, { taskToken: s.token }).then(
      (r) => r.state,
    ),

  upload: (s: SavedSubmission, file: File, note: string, onProgress?: OnProgress) =>
    putFile<{ state: TaskSubmissionState }>(
      `/task-submissions/${s.submissionId}/file?name=${encodeURIComponent(file.name)}`,
      file,
      { taskToken: s.token, headers: { 'X-Task-Note': encodeURIComponent(note) }, onProgress },
    ).then((r) => r.state),

  withdraw: (s: SavedSubmission) =>
    call<{ state: TaskSubmissionState }>(`/task-submissions/${s.submissionId}/file`, {
      method: 'DELETE',
      taskToken: s.token,
    }).then((r) => r.state),

  ownFile: (s: SavedSubmission) =>
    call<Blob>(`/task-submissions/${s.submissionId}/file`, { taskToken: s.token, asBlob: true }),

  ownMaterial: (s: SavedSubmission) =>
    call<Blob>(`/task-submissions/${s.submissionId}/material`, { taskToken: s.token, asBlob: true }),

  attachment: (code: string) =>
    call<Blob>('/task-access/attachment', { method: 'POST', body: JSON.stringify({ code }), asBlob: true }),

  /* Lado de quien crea la tarea (con sesión) */

  uploadAttachment: (taskId: string, file: File, onProgress?: OnProgress) =>
    putFile<{ task: TaskSummary }>(`/tasks/${taskId}/attachment?name=${encodeURIComponent(file.name)}`, file, {
      onProgress,
    }).then((r) => r.task),

  uploadMaterial: (taskId: string, studentId: string, file: File, onProgress?: OnProgress) =>
    putFile<{ material: FileInfo }>(
      `/tasks/${taskId}/materials/${studentId}?name=${encodeURIComponent(file.name)}`,
      file,
      { onProgress },
    ).then((r) => r.material),

  material: (taskId: string, studentId: string) =>
    call<Blob>(`/tasks/${taskId}/materials/${studentId}/file`, { asBlob: true }),

  submissionFile: (taskId: string, submissionId: string) =>
    call<Blob>(`/tasks/${taskId}/submissions/${submissionId}/file`, { asBlob: true }),
};

/** Compara nombres sin mayúsculas, tildes ni espacios de más. */
export const nameKey = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * De quién es un archivo según su nombre: «Practica - Ana Pérez.docx» → «Ana
 * Pérez». Se queda con lo que va tras el último « - », sin la extensión.
 */
export function nameFromFile(fileName: string): string {
  const base = fileName.replace(/\.(pdf|docx?)$/i, '');
  const parts = base.split(/\s+[-–]\s+/);
  return (parts.length > 1 ? parts[parts.length - 1] : base).replace(/[_]+/g, ' ').trim();
}

export function formatBytes(n: number | null | undefined): string {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

export function formatDue(ms: number): string {
  return new Date(ms).toLocaleString('es', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
